import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
  QueryConstraint,
} from 'firebase/firestore';
import { db } from '../../../utils/init-firebase';
import { ActionItem, User, Rock, COLLECTION_NAMES } from '../types';

export interface CalendarActionItem extends ActionItem {
  owner?: User;
  linkedRock?: Rock;
}

export interface UseCalendarActionItemsParams {
  startDate: Date;
  endDate: Date;
  selectedUserId?: string; // 'all' or specific userId
}

/**
 * Hook for fetching action items within a date range for calendar display
 * Supports filtering by user (ownerId or assignedToUserId)
 * Joins with users and rocks for display
 */
export function useCalendarActionItems({ startDate, endDate, selectedUserId }: UseCalendarActionItemsParams) {
  const [actionItems, setActionItems] = useState<ActionItem[]>([]);
  const { eosUsers = [] } = useAuth() as { eosUsers: User[] };
  const [rocks, setRocks] = useState<Rock[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Memoize dates for dependency comparison
  const startDateObj = useMemo(
    () => startDate instanceof Date ? startDate : new Date(startDate),
    [startDate]
  );
  const endDateObj = useMemo(
    () => endDate instanceof Date ? endDate : new Date(endDate),
    [endDate]
  );

  // Load rocks for joining
  useEffect(() => {
    const rocksQuery = query(collection(db, COLLECTION_NAMES.ROCKS));
    const unsubscribe = onSnapshot(
      rocksQuery,
      (snapshot) => {
        const rocksData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          dueDate: doc.data().dueDate?.toDate?.() || doc.data().dueDate,
          createdAt: doc.data().createdAt || Timestamp.now(),
          updatedAt: doc.data().updatedAt || Timestamp.now(),
        })) as Rock[];
        setRocks(rocksData);
      },
      (error) => {
        console.error('Error loading rocks:', error);
      }
    );

    return () => unsubscribe();
  }, []);

  // Load action items with date range and user filtering
  useEffect(() => {
    setLoading(true);

    // Base query constraints
    const baseConstraints: QueryConstraint[] = [
      where('dropped', '==', false),
      where('dueDate', '>=', startDateObj),
      where('dueDate', '<=', endDateObj),
      orderBy('dueDate', 'asc'),
    ];

    // Handle user filtering
    // Firestore doesn't support OR queries efficiently, so we'll:
    // 1. If selectedUserId is 'all' or undefined, fetch all todos in range with one listener
    // 2. If selectedUserId is set, use two listeners (ownerId and assignedToUserId) and merge
    if (selectedUserId && selectedUserId !== 'all') {
      // Two separate queries for OR condition
      const ownerQuery = query(
        collection(db, COLLECTION_NAMES.ACTION_ITEMS),
        ...baseConstraints,
        where('ownerId', '==', selectedUserId)
      );

      const assignedQuery = query(
        collection(db, COLLECTION_NAMES.ACTION_ITEMS),
        ...baseConstraints,
        where('assignedToUserId', '==', selectedUserId)
      );

      // Track action items from both queries
      let ownerActionItems: ActionItem[] = [];
      let assignedActionItems: ActionItem[] = [];
      let ownerLoaded = false;
      let assignedLoaded = false;

      const updateMergedActionItems = () => {
        if (ownerLoaded && assignedLoaded) {
          // Merge and remove duplicates by id
          const allActionItems = [...ownerActionItems, ...assignedActionItems];
          const uniqueActionItems = Array.from(
            new Map(allActionItems.map((actionItem) => [actionItem.id, actionItem])).values()
          );
          setActionItems(uniqueActionItems);
          setLoading(false);
        }
      };

      const unsubscribeOwner = onSnapshot(
        ownerQuery,
        (snapshot) => {
          ownerActionItems = snapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              dueDate: data.dueDate?.toDate?.() || data.dueDate,
              completedDate: data.completedDate?.toDate?.() || data.completedDate,
              createdAt: data.createdAt || Timestamp.now(),
              updatedAt: data.updatedAt || Timestamp.now(),
            } as ActionItem;
          });
          ownerLoaded = true;
          updateMergedActionItems();
        },
        (error) => {
          console.error('Error loading owner action items:', error);
          ownerLoaded = true;
          updateMergedActionItems();
        }
      );

      const unsubscribeAssigned = onSnapshot(
        assignedQuery,
        (snapshot) => {
          assignedActionItems = snapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              dueDate: data.dueDate?.toDate?.() || data.dueDate,
              completedDate: data.completedDate?.toDate?.() || data.completedDate,
              createdAt: data.createdAt || Timestamp.now(),
              updatedAt: data.updatedAt || Timestamp.now(),
            } as ActionItem;
          });
          assignedLoaded = true;
          updateMergedActionItems();
        },
        (error) => {
          console.error('Error loading assigned action items:', error);
          assignedLoaded = true;
          updateMergedActionItems();
        }
      );

      return () => {
        unsubscribeOwner();
        unsubscribeAssigned();
      };
    } else {
      // Fetch all action items in date range
      const actionItemsQuery = query(collection(db, COLLECTION_NAMES.ACTION_ITEMS), ...baseConstraints);

      const unsubscribe = onSnapshot(
        actionItemsQuery,
        (snapshot) => {
          const actionItemsData = snapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              dueDate: data.dueDate?.toDate?.() || data.dueDate,
              completedDate: data.completedDate?.toDate?.() || data.completedDate,
              createdAt: data.createdAt || Timestamp.now(),
              updatedAt: data.updatedAt || Timestamp.now(),
            } as ActionItem;
          });
          setActionItems(actionItemsData);
          setLoading(false);
        },
        (error) => {
          console.error('Error loading action items:', error);
          setLoading(false);
        }
      );

      return () => unsubscribe();
    }
  }, [startDateObj, endDateObj, selectedUserId]);

  // Join action items with users and rocks
  const actionItemsWithJoins = useMemo(() => {
    const usersMap = new Map(eosUsers.map((user) => [user.id, user]));
    const rocksMap = new Map(rocks.map((rock) => [rock.id, rock]));

    return actionItems.map((actionItem) => {
      const calendarActionItem: CalendarActionItem = {
        ...actionItem,
        owner: actionItem.ownerId ? usersMap.get(actionItem.ownerId) : undefined,
        linkedRock: actionItem.linkedRockId ? rocksMap.get(actionItem.linkedRockId) : undefined,
      };
      return calendarActionItem;
    });
  }, [actionItems, eosUsers, rocks]);

  return {
    actionItems: actionItemsWithJoins,
    loading,
  };
}

